import { useMemo, useState } from "react";
import AppButton from "../../../components/ui/AppButton";
import { useToast } from "../../../hooks/useToast";
import SettingsCard from "../ui/SettingsCard";
import SettingsToggle from "../ui/SettingsToggle";

const STORAGE_KEY = "fleetpro.admin.notificationPrefs";

type NotificationPrefs = {
  newUserRegistrations: boolean;
  systemUpdates: boolean;
  weeklyReports: boolean;
  securityAlerts: boolean;
  desktop: boolean;
  mobile: boolean;
};

const DEFAULT_PREFS: NotificationPrefs = {
  newUserRegistrations: true,
  systemUpdates: true,
  weeklyReports: false,
  securityAlerts: true,
  desktop: true,
  mobile: true,
};

const loadPrefs = (): NotificationPrefs => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFS;
  }
};

const NotificationsContent = () => {
  const toast = useToast();
  const saved = useMemo(() => loadPrefs(), []);
  const [prefs, setPrefs] = useState<NotificationPrefs>(saved);
  const [baseline, setBaseline] = useState<NotificationPrefs>(saved);

  const isDirty = JSON.stringify(prefs) !== JSON.stringify(baseline);

  const setPref = (key: keyof NotificationPrefs, value: boolean) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setBaseline(prefs);
    toast.success({
      title: "Preferences saved",
      message: "Your notification settings have been updated.",
    });
  };

  const handleCancel = () => {
    setPrefs(baseline);
  };

  return (
    <div className="space-y-6">
      <SettingsCard
        title="Email notifications"
        description="Choose which emails we send to your inbox."
        bodyClassName="px-6 py-2 divide-y divide-[#F1F1EE]"
      >
        <SettingsToggle
          label="New user registrations"
          description="Get notified when a teammate is added to this company."
          checked={prefs.newUserRegistrations}
          onChange={(value) => setPref("newUserRegistrations", value)}
        />
        <SettingsToggle
          label="System updates"
          description="Product changes and maintenance that may affect your fleet."
          checked={prefs.systemUpdates}
          onChange={(value) => setPref("systemUpdates", value)}
        />
        <SettingsToggle
          label="Weekly reports"
          description="A digest of trips, spend, and vehicle activity."
          checked={prefs.weeklyReports}
          onChange={(value) => setPref("weeklyReports", value)}
        />
        <SettingsToggle
          label="Security alerts"
          description="Sign-ins from new devices and password changes."
          checked={prefs.securityAlerts}
          onChange={(value) => setPref("securityAlerts", value)}
        />
      </SettingsCard>

      <SettingsCard
        title="Push notifications"
        description="Alerts that appear on this computer or a linked device."
        bodyClassName="px-6 py-2 divide-y divide-[#F1F1EE]"
      >
        <SettingsToggle
          label="Desktop notifications"
          description="Show alerts in the header for requisitions, incidents, and maintenance."
          checked={prefs.desktop}
          onChange={(value) => setPref("desktop", value)}
        />
        <SettingsToggle
          label="Mobile push"
          description="Send the same alerts to the FleetPro mobile app."
          checked={prefs.mobile}
          onChange={(value) => setPref("mobile", value)}
        />
      </SettingsCard>

      <div className="flex justify-end gap-3">
        <AppButton
          type="button"
          appearance="soft"
          variant="secondary"
          onClick={handleCancel}
          disabled={!isDirty}
          className="h-[42px] px-5"
        >
          Cancel
        </AppButton>
        <AppButton
          type="button"
          onClick={handleSave}
          disabled={!isDirty}
          className="h-[42px] px-5"
        >
          Save changes
        </AppButton>
      </div>
    </div>
  );
};

export default NotificationsContent;
